import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Friendly names for the raw `source` strings feed items, segments and runs carry.
 *
 * Items store `granola:<folderId>` / `slack:<channelId>` because that string is a stable machine
 * identity — automations filter on it, and a renamed channel must keep matching — but rendered
 * as-is it reads as noise. The names live on the IntegrationSource rows, so one fetch per session
 * maps every raw string to what its folder or channel is currently called, and old rows relabel
 * for free when something upstream is renamed.
 *
 * Module-level cache rather than a context: a dozen components render sources, and every one of
 * them needing a provider above it is a lot of plumbing for a map that never changes mid-session.
 */
let cache: Record<string, string> | null = null;
let inFlight: Promise<Record<string, string>> | null = null;

export function useSourceLabels(): Record<string, string> {
  const [labels, setLabels] = useState<Record<string, string>>(cache ?? {});

  useEffect(() => {
    if (cache) return;
    inFlight ??= api.integrations.sourceLabels().then((result) => (cache = result));
    let mounted = true;
    // A failure just leaves the raw strings on screen, which is exactly what rendered before.
    inFlight.then((result) => mounted && setLabels(result)).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return labels;
}

/**
 * The display form of a source string: the watched folder or channel's name when we know it
 * (`#`-prefixed for Slack, since that is how a channel is read everywhere), the raw string when we
 * don't — a source no longer watched has no name to offer, and an invented one would be worse.
 */
export function formatSource(source: string | null | undefined, labels: Record<string, string>): string {
  if (!source) return '';
  const name = labels[source];
  if (!name) return source;
  return source.startsWith('slack') ? `#${name}` : name;
}

/**
 * Any raw source string appearing *inside* a longer label, so it can be resolved in place.
 *
 * `slack-private` is listed ahead of `slack` so the longer prefix wins the alternation outright
 * rather than by backtracking.
 *
 * The character class carries `.@:` for the two Google sources: a mail source is an address, and
 * may be `address:Label_7`. Greedy is right there — a shorter match would resolve the whole-mailbox
 * label for a watch that is actually one label inside it.
 */
const SOURCE_TOKEN = /\b(?:slack-private|slack|granola|mail|drive):[A-Za-z0-9_.@:-]+/g;

/**
 * A run's stored label, with any raw source string inside it resolved to a name.
 *
 * A batch names its biggest contributors ("Thrasos kickoff, slack:C08ENG +4 (27 items · GRANOLA,
 * SLACK)"), and the ones that arrived with no title of their own — every Slack message — are named
 * by the same id string items carry. The run row is written the moment the batch is packed, long
 * before anyone looks at it, so resolving the name there would freeze it at whatever the channel
 * was called that night; doing it here means a renamed channel relabels every batch it ever
 * appeared in, exactly as `formatSource` already does for a single source.
 *
 * A label with no source-shaped token in it — a meeting title, a file name — passes through
 * untouched, which is every run that isn't a batch.
 */
export function formatRunLabel(label: string | null | undefined, labels: Record<string, string>): string {
  if (!label) return '';
  return label.replace(SOURCE_TOKEN, (raw) => formatSource(raw, labels));
}

/**
 * A citation source as one line: "Daily Standup · Client meetings" — the item's own title, then
 * the folder or channel it arrived through, whichever halves are actually known. The raw id string
 * appears only when nothing better exists on either side, which is what it looked like before any
 * of this.
 */
export function formatCitationSource(
  source: { label: string; raw?: string | null },
  labels: Record<string, string>,
): string {
  const raw = source.raw ?? null;
  // The server sends label = title when the item has one, and label = raw source when it doesn't.
  const title = source.label && source.label !== raw ? source.label : null;
  const place = raw ? formatSource(raw, labels) : null;
  const placeNamed = place !== null && place !== raw ? place : null;

  if (title && placeNamed) return `${title} · ${placeNamed}`;
  if (title) return title;
  return placeNamed ?? raw ?? '';
}
